import React from 'react';
import { NextSeo } from 'next-seo';

import AppCustomLink from '../components/Common/AppCustomLink';
import { spacing, maxScreenSize } from '../lib/theme';

const appName = 'Meeting Log AI';
const companyName = 'Ted Labs';
const website = 'https://meetinglog.ai';
const supportEmail = 'support@tedlabs.ai';
const lastUpdated = 'October 2, 2026';

export default function PrivacyPolicyPage() {
    return (
        <div className='legal-page'>
            <NextSeo
                title={`Privacy Policy | ${appName}`}
                description={`How ${appName} collects, uses and protects your recordings, transcripts and personal data.`}
                canonical={`${website}/privacy-policy`}
            />
            <article className='legal-content'>
                <h1>Privacy Policy</h1>
                <p className='updated'>Last updated: {lastUpdated}</p>

                <p>{appName} (&quot;{appName}&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;) is operated by {companyName}, London, United Kingdom. This Privacy Policy explains what information we collect when you use our website at <a href={website}>{website}</a> and our iOS, Android and macOS apps (together, the &quot;Service&quot;), how we use it, and the choices you have.</p>
                <p>Your recordings and notes are personal. We will never sell your data, and we only use it to provide and improve the Service as described below.</p>

                <h2>1. Information We Collect</h2>
                <h3>Information you provide</h3>
                <ul>
                    <li><strong>Account information</strong> such as your name, email address and sign-in details.</li>
                    <li><strong>Audio content</strong> you record in the app, upload (for example mp3, m4a or wav files) or link to (for example a YouTube URL).</li>
                    <li><strong>Generated content</strong> such as transcripts, summaries, notes, titles and any edits you make to them.</li>
                    <li><strong>Support communications</strong> when you email us or send feedback.</li>
                </ul>
                <h3>Information collected automatically</h3>
                <ul>
                    <li><strong>Device and usage data</strong> such as device type, operating system, app version, language preference, IP address, crash reports and how you interact with features.</li>
                    <li><strong>Cookies and similar technologies</strong> on our website. See our <AppCustomLink href='/cookies-details'><a>Cookies</a></AppCustomLink> page for details.</li>
                </ul>
                <h3>Purchase information</h3>
                <p>Subscriptions purchased in the app are processed by Apple (App Store) or Google (Google Play). We receive confirmation of your subscription status but do not receive or store your full payment card details.</p>

                <h2>2. Microphone and Recording Permissions</h2>
                <p>The app requests access to your microphone only so you can record audio. Recording starts and stops only when you choose. You are responsible for informing and obtaining consent from other participants before recording a conversation or meeting where the law requires it.</p>

                <h2>3. How We Use Your Information</h2>
                <ul>
                    <li>To transcribe, summarize, translate and organize your recordings.</li>
                    <li>To create and manage your account and sync your content across devices.</li>
                    <li>To process subscriptions and provide customer support.</li>
                    <li>To maintain security, prevent abuse and fix bugs.</li>
                    <li>To understand aggregate usage and improve the Service.</li>
                    <li>To send you service-related messages and, where permitted, occasional product updates. You can opt out of marketing emails at any time.</li>
                </ul>
                <p>We do not use your recordings, transcripts or notes to train our own or third-party AI models.</p>

                <h2>4. AI Processing and Service Providers</h2>
                <p>To transcribe and summarize audio, your content is sent securely to trusted cloud and AI service providers that process it on our behalf. These providers act as data processors, are bound by contractual confidentiality and data protection obligations, and may use your content only to provide the Service to us.</p>
                <p>We also use service providers for hosting, storage, authentication, analytics and crash reporting. We share only the information they need to perform these services.</p>

                <h2>5. How We Share Information</h2>
                <p>We do not sell or rent your personal information. We share it only:</p>
                <ul>
                    <li>With service providers as described above.</li>
                    <li>When you choose to share or export a recording, transcript or summary.</li>
                    <li>When required by law, court order or government request, or to protect the rights, property or safety of {appName}, our users or the public.</li>
                    <li>In connection with a merger, acquisition or sale of assets, in which case we will notify you before your information becomes subject to a different privacy policy.</li>
                </ul>

                <h2>6. Data Retention and Deletion</h2>
                <p>We keep your content for as long as your account is active or as needed to provide the Service. You can delete individual recordings and notes in the app at any time, and you can permanently delete your account and all associated data from the app settings or by emailing <a href={`mailto:${supportEmail}?subject=Data%20deletion`}>{supportEmail}</a> with &lsquo;Data deletion&rsquo; in the subject line. Deleted data is removed from our active systems promptly and from backups within a reasonable period.</p>

                <h2>7. Security</h2>
                <p>We use industry-standard measures to protect your data, including encryption in transit and access controls. No method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.</p>

                <h2>8. Your Rights (GDPR and CCPA)</h2>
                <p>Depending on where you live, you may have the right to access, correct, delete or export your personal data, to object to or restrict certain processing, and to withdraw consent. California residents have the right to know what personal information we collect and to request its deletion, and we do not sell or share personal information for cross-context behavioral advertising. To exercise any of these rights, contact us at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>. You also have the right to lodge a complaint with your local data protection authority, such as the UK Information Commissioner&apos;s Office.</p>
                <p>Where GDPR applies, we process your data to perform our contract with you (providing the Service), for our legitimate interests (security and improvement), to comply with legal obligations, and with your consent where required.</p>

                <h2>9. International Transfers</h2>
                <p>Your information may be processed in countries other than your own, including the United States. Where we transfer personal data outside the UK or EEA, we rely on appropriate safeguards such as Standard Contractual Clauses.</p>

                <h2>10. Children&apos;s Privacy</h2>
                <p>The Service is not directed to children under 13 (or under 16 where required by local law), and we do not knowingly collect their personal information. If you believe a child has provided us with personal data, please contact us and we will delete it.</p>

                <h2>11. Links to Other Sites</h2>
                <p>The Service may contain links to third-party websites. We are not responsible for their content or privacy practices, and we encourage you to review their privacy policies.</p>

                <h2>12. Changes to This Policy</h2>
                <p>We may update this Privacy Policy from time to time. We will post the updated version on this page and change the &quot;Last updated&quot; date above. For material changes we will provide additional notice in the app or by email.</p>

                <h2>13. Contact Us</h2>
                <p>If you have any questions about this Privacy Policy or how we handle your data, please contact us:</p>
                <p>
                    {companyName}<br />
                    London, United Kingdom<br />
                    Email: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </p>
                <p>See also our <AppCustomLink href='/terms-and-conditions'><a>Terms and Conditions</a></AppCustomLink>.</p>
            </article>
            <style jsx>
                {`
                    .legal-page {
                        padding: ${spacing('xxl')} ${spacing('lg')};
                    }
                    .legal-content {
                        max-width: 820px;
                        margin: auto;
                        background: white;
                        padding: ${spacing('xxl')};
                        border-radius: 12px;
                        line-height: 1.7;
                    }
                    .legal-content h2 {
                        margin-top: 2em;
                    }
                    .legal-content h3 {
                        margin-top: 1.25em;
                    }
                    .updated {
                        color: #6b7280;
                    }
                    @media ${maxScreenSize.tablet} {
                        .legal-page {
                            padding: ${spacing('lg')} ${spacing()};
                        }
                        .legal-content {
                            padding: ${spacing('lg')};
                        }
                    }
                `}
            </style>
        </div>
    )
}
